/**
 * Focus bridge for the auth (Sign in / Create account) modal.
 *
 * The modal is rendered globally in `pages/_app.jsx` (`{show && <AuthModal />}`),
 * while its triggers ("Sign in" / "Create account") are rendered by the external
 * DSM `MainMenu` in the header. Those two trees are only connected through redux,
 * which cannot hold DOM nodes — so we bridge the triggering element through these
 * module-scoped refs instead.
 *
 * Capturing happens synchronously at click time (before the DSM dropdown
 * re-renders / collapses the trigger), and focus is restored when the modal
 * closes. Mirrors the restore pattern already used in
 * `components/Default/PromoBanner/v1/PromoModal.jsx` and `hooks/useFocusTrap.js`.
 */

// The element that opened the modal (the Sign in / Create account button).
export const authModalTriggerRef = { current: null }
// Fallback: the trigger of whatever menu was open when the modal opened. It stays
// focusable even if the dropdown collapses and makes the primary trigger inert.
export const authModalMenuTriggerRef = { current: null }

/**
 * Records the element that is about to open the auth modal. Call this from the
 * trigger's click handler, before dispatching the open action.
 */
export function captureAuthModalTrigger() {
  if (typeof document === "undefined") return
  const active = document.activeElement
  authModalTriggerRef.current =
    active && active !== document.body ? active : null
  authModalMenuTriggerRef.current = document.querySelector(
    '[aria-controls="login-menu"][aria-expanded="true"], ' +
      '[aria-haspopup][aria-expanded="true"]'
  )
}

/**
 * Returns focus to the element that opened the auth modal when it closes.
 * Tries the exact trigger first, then the open-menu trigger, then the element
 * captured by the core Modal (covers openers outside the header). Skips any
 * candidate that is detached or non-focusable (e.g. an inert dropdown button).
 */
export function returnFocusToAuthModalTrigger() {
  if (typeof document === "undefined") return
  const candidates = [
    authModalTriggerRef.current,
    authModalMenuTriggerRef.current,
    typeof window !== "undefined" ? window.prevActiveElement : null,
  ]
  for (const el of candidates) {
    if (el && el.isConnected && typeof el.focus === "function") {
      el.focus()
      // Confirm focus actually landed (an inert/hidden element silently rejects it).
      if (document.activeElement === el) break
    }
  }
  authModalTriggerRef.current = null
  authModalMenuTriggerRef.current = null
}
